import { SectionEmptyState } from '../../../components/ui/States';
import { ShareIcon } from '../../../components/ui/icons';
import {
  useNavigation,
  useNavigationState,
} from '../../navigation/NavigationProvider';
import { useUnsavedChangesGuard } from '../../../hooks/useUnsavedChangesGuard';
import { DialogContent } from '../../../components/ui/DialogContent';
import { DialogActions } from '../../../components/ui/DialogActions';
import { DialogHeader } from '../../../components/ui/DialogHeader';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  DialogContentText,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {
  CollectionMemberSummary,
  CollectionTransferResult,
} from '@qkindex/contracts';
import { ResponsiveDialog } from '../../../components/ui/ResponsiveDialog';
import { createCollectionPeopleController } from '../collectionPeopleService';
import {
  collectionTransferErrorMessage,
  createCollectionTransferController,
} from '../collectionTransferService';

type TransferAction = 'start' | 'accept' | 'cancel' | 'expire';

interface CollectionTransferSectionProps {
  collectionId: string;
  collectionName?: string;
  onCancel?: () => void;
  userId: string;
  role: 'owner' | 'administrator' | 'editor';
  onChanged(): Promise<void> | void;
}

const safeName = (value: string): string =>
  Array.from(value, (character) => {
    const codePoint = character.codePointAt(0) || 0;
    return codePoint <= 0x1f ||
      (codePoint >= 0x7f && codePoint <= 0x9f) ||
      codePoint === 0x61c ||
      codePoint === 0x200e ||
      codePoint === 0x200f ||
      (codePoint >= 0x2028 && codePoint <= 0x202e) ||
      (codePoint >= 0x2066 && codePoint <= 0x2069)
      ? ' '
      : character;
  })
    .join('')
    .replace(/\s+/gu, ' ')
    .trim() || 'Member';

export function CollectionTransferSection({
  collectionId,
  collectionName = 'Active collection',
  onCancel,
  userId,
  role,
  onChanged,
}: CollectionTransferSectionProps) {
  const controller = useMemo(() => createCollectionTransferController(), []);
  const peopleController = useMemo(
    () => createCollectionPeopleController(),
    [],
  );
  const [transfer, setTransfer] = useState<CollectionTransferResult | null>();
  const [editors, setEditors] = useState<CollectionMemberSummary[]>([]);
  const [memberCursor, setMemberCursor] = useState<string>();
  const [selectedUserId, setSelectedUserId] = useState('');
  const scopeKey = `collection.transfer.${collectionId}.${userId}`;
  const { replaceState } = useNavigation();
  const [confirmation, setConfirmation] = useNavigationState<
    { action: TransferAction; recipientId?: string } | undefined
  >(scopeKey, undefined);
  const confirmAction = confirmation?.action;
  const setConfirmAction = (action?: TransferAction) =>
    setConfirmation(
      action
        ? {
            action,
            ...(action === 'start' ? { recipientId: selectedUserId } : {}),
          }
        : undefined,
    );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const { requestClose, runAfterSave } = useUnsavedChangesGuard({
    scopeKey,
    open: Boolean(confirmAction),
    dirty: false,
    pending: busy,
    onDiscard: () => {},
    onClose: () => setConfirmAction(undefined),
  });
  const dismissConfirmation = () => {
    void requestClose();
  };
  const activeCollection = useRef(collectionId);
  activeCollection.current = collectionId;
  const loadGeneration = useRef(0);

  const loadEditors = async (append = false) => {
    const requestedCollection = collectionId;
    const requestedGeneration = loadGeneration.current;
    const people = await peopleController.list(
      collectionId,
      append && memberCursor ? { memberCursor } : {},
    );
    if (
      activeCollection.current !== requestedCollection ||
      loadGeneration.current !== requestedGeneration
    )
      return;
    const eligible = people.members.items.filter(
      (member: CollectionMemberSummary) =>
        member.status === 'active' &&
        (member.role === 'editor' || member.role === 'administrator'),
    );
    setEditors((current) => (append ? [...current, ...eligible] : eligible));
    setMemberCursor(people.members.nextCursor);
    setSelectedUserId((current) =>
      [...(append ? editors : []), ...eligible].some(
        ({ userId: id }) => id === current,
      )
        ? current
        : '',
    );
  };

  const load = async () => {
    const requestedCollection = collectionId;
    const requestedGeneration = loadGeneration.current;
    setError(undefined);
    try {
      const active = await controller.get(collectionId);
      if (
        activeCollection.current !== requestedCollection ||
        loadGeneration.current !== requestedGeneration
      )
        return;
      setTransfer(active);

      if (!active && role === 'owner') {
        await loadEditors();
        if (activeCollection.current !== requestedCollection) return;
      }
    } catch (loadError) {
      if (activeCollection.current === requestedCollection) {
        setError(collectionTransferErrorMessage(loadError));
      }
    }
  };

  useEffect(() => {
    loadGeneration.current += 1;
    controller.reset();
    peopleController.reset();
    setTransfer(undefined);
    setEditors([]);
    setMemberCursor(undefined);
    setSelectedUserId('');
    void load();
    return () => {
      loadGeneration.current += 1;
      controller.reset();
      peopleController.reset();
    };
    // Controllers are stable and collection-bound state is reset on switches.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionId, role, controller, peopleController]);

  const loadMoreEditors = async () => {
    if (busy || !memberCursor) return;
    setBusy(true);
    setError(undefined);
    try {
      await loadEditors(true);
    } catch (loadError) {
      setError(collectionTransferErrorMessage(loadError));
    } finally {
      setBusy(false);
    }
  };

  const run = async () => {
    if (!confirmAction || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      let result: CollectionTransferResult;
      if (confirmAction === 'start') {
        const recipient = editors.find(
          ({ userId: id }) => id === confirmation?.recipientId,
        );
        if (!recipient)
          throw new Error(
            'Collection or recipient changed. Refresh and try again.',
          );
        result = await controller.start(collectionId, recipient);
      } else {
        if (!transfer) throw new Error('Transfer changed.');
        result = await controller.mutate(confirmAction, transfer);
      }
      controller.acknowledge(confirmAction);
      runAfterSave(() => replaceState(scopeKey, undefined));
      if (
        result.status === 'complete' ||
        result.status === 'cancelled' ||
        result.status === 'expired'
      ) {
        setTransfer(null);
      } else {
        setTransfer(result);
      }
      await onChanged();
    } catch (mutationError) {
      runAfterSave(() => replaceState(scopeKey, undefined));
      setError(collectionTransferErrorMessage(mutationError));
    } finally {
      setBusy(false);
    }
  };

  const terms =
    'Items contribute to the new owner’s account total and monthly peak. The previous owner’s accrued peak and debt remain theirs. Both owners may be billed for these items at their own monthly anniversaries. There is no proration or immediate charge.';
  const recipient = transfer?.toUserId === userId;
  const action = transfer
    ? transfer.expiresAt.seconds * 1000 <= Date.now()
      ? 'expire'
      : role === 'owner'
        ? 'cancel'
        : recipient && transfer.status === 'pending-recipient'
          ? 'accept'
          : undefined
    : undefined;
  const validConfirmation =
    confirmAction === 'start'
      ? transfer === null &&
        role === 'owner' &&
        editors.some((member) => member.userId === confirmation?.recipientId)
      : Boolean(confirmAction && confirmAction === action);
  useEffect(() => {
    if (transfer !== undefined && confirmAction && !validConfirmation && !busy)
      replaceState(scopeKey, undefined);
  }, [
    transfer,
    confirmAction,
    validConfirmation,
    busy,
    replaceState,
    scopeKey,
  ]);
  const labels: Record<
    TransferAction,
    { button: string; title: string; body: string }
  > = {
    start: {
      button: 'Start transfer',
      title: 'Start ownership transfer?',
      body: `The collection stays active under your account until the recipient accepts. Items can be added, edited or deleted while the offer is open. ${terms}`,
    },
    accept: {
      button: 'Accept transfer',
      title: 'Accept ownership transfer?',
      body: `${terms} You accept the collection as it exists when you confirm, including items added or deleted since the offer. Ownership and billing change together, and the previous owner becomes an editor.`,
    },
    cancel: {
      button: 'Cancel transfer',
      title: 'Cancel ownership transfer?',
      body: 'This cancels the offer. The current owner and normal collection activity remain unchanged.',
    },
    expire: {
      button: 'Release expired transfer',
      title: 'Release expired transfer?',
      body: 'This closes the expired offer without changing ownership or collection activity.',
    },
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}
          {transfer === undefined && !error && (
            <Typography variant="body2">Loading transfer…</Typography>
          )}
          {error && (
            <Button
              onClick={() => {
                void load();
              }}
            >
              Reload transfer
            </Button>
          )}

          {transfer === null && role === 'owner' && (
            <Typography>
              Choose a new owner for the{' '}
              <strong>
                <bdi dir="auto">{collectionName}</bdi>
              </strong>{' '}
              collection. They’ll need to accept the transfer before you become
              an editor.
            </Typography>
          )}
          {transfer && (
            <Typography variant="body2">
              Transfer status: {transfer.status}. It expires automatically if it
              is not completed.
            </Typography>
          )}
          {transfer === null &&
            role === 'owner' &&
            (editors.length ? (
              <FormControl fullWidth>
                <InputLabel shrink id="transfer-recipient-label">
                  New owner
                </InputLabel>
                <Select
                  labelId="transfer-recipient-label"
                  label="New owner"
                  notched
                  value={selectedUserId}
                  displayEmpty
                  renderValue={(value) =>
                    value
                      ? safeName(
                          editors.find((member) => member.userId === value)
                            ?.displayName || 'Member',
                        )
                      : 'Choose an editor or administrator'
                  }
                  disabled={busy}
                  onChange={(event) => setSelectedUserId(event.target.value)}
                >
                  <MenuItem value="" disabled>
                    Choose an editor or administrator
                  </MenuItem>
                  {editors.map((member) => (
                    <MenuItem key={member.userId} value={member.userId}>
                      {safeName(member.displayName)} ·{' '}
                      {member.role === 'administrator'
                        ? 'Administrator'
                        : 'Editor'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Add an editor or administrator before transferring ownership.
              </Typography>
            ))}
          {transfer === null &&
            (role === 'editor' || role === 'administrator') && (
              <SectionEmptyState
                compact
                icon={ShareIcon}
                message="No pending transfer."
              />
            )}
          {transfer === null && role === 'owner' && memberCursor && (
            <Button
              disabled={busy}
              onClick={() => {
                void loadMoreEditors();
              }}
            >
              Load more eligible members
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onCancel}>
          {transfer ? 'Close' : 'Cancel'}
        </Button>
        {transfer === null && role === 'owner' && (
          <Button
            variant="contained"
            disabled={busy || !selectedUserId}
            onClick={() => setConfirmAction('start')}
          >
            Start transfer
          </Button>
        )}
        {action && (
          <Button
            variant="contained"
            color={action === 'cancel' ? 'error' : 'primary'}
            disabled={busy}
            onClick={() => setConfirmAction(action)}
          >
            {labels[action].button}
          </Button>
        )}
      </DialogActions>
      <ResponsiveDialog
        open={Boolean(confirmAction) && validConfirmation}
        onClose={dismissConfirmation}
        aria-labelledby="transfer-confirm-title"
      >
        <DialogHeader
          id="transfer-confirm-title"
          title={confirmAction ? labels[confirmAction].title : ''}
        />
        <DialogContent>
          <DialogContentText>
            {confirmAction ? labels[confirmAction].body : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={dismissConfirmation}>
            Back
          </Button>
          <Button
            variant="contained"
            color={confirmAction === 'cancel' ? 'error' : 'primary'}
            disabled={busy}
            onClick={() => {
              void run();
            }}
          >
            {busy
              ? 'Working…'
              : confirmAction
                ? labels[confirmAction].button
                : 'Confirm'}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </>
  );
}
